<template>
  <div class="grid grid-cols-1 lg:grid-cols-2 gap-2 lg:gap-6">
    <div
      v-for="type of investmentTypes"
      :key="type.name"
      @click="isDisabled(type.name) ? null : selectType(type.value)"
      class="p-3 lg:p-6 bg-container-bg rounded-xl text-strongest col-span-1 flex flex-col lg:flex-row justify-between lg:items-center font-medium h-fit lg:h-[250px] xl:h-[200px] cursor-pointer border-2 overflow-hidden"
      :class="[
        isSelected(type) ? 'border-radio-checkbox-outline' : 'border-transparent',
        isDisabled(type.name) ? 'opacity-50 !cursor-not-allowed' : 'cursor-pointer',
      ]"
    >
      <div class="flex justify-between items-start lg:block relative">
        <div>
          <p class="text-lg lg:text-[28px] mb-3">{{ type.name }}</p>

          <!-- Desktop description -->
          <p class="hidden lg:block text-sm text-strong font-inter mr-24">
            {{ type.description }}
          </p>

          <!-- Mobile "See Details" toggle -->
          <button
            @click.stop="isDisabled(type.name) ? null : toggleDetails(type.name)"
            class="lg:hidden flex items-center gap-1 text-sm text-strong"
          >
            <span class="font-inter">{{
              expandedType === type.name ? "Hide Details" : "See Details"
            }}</span>
            <Icon
              name="ChevronDown"
              class="w-4 h-4 transition-transform duration-200"
              :class="{ 'rotate-180': expandedType === type.name }"
            />
          </button>
        </div>

        <!-- Image on mobile (top right) -->
        <img
          :src="getImageSrc(type.name)"
          alt=""
          class="lg:hidden h-24 object-contain absolute -right-[35px] -bottom-[30px]"
        />
      </div>

      <!-- Mobile expanded description -->
      <Transition
        enter-active-class="transition-all duration-200 ease-out"
        leave-active-class="transition-all duration-200 ease-in"
        enter-from-class="opacity-0 max-h-0"
        enter-to-class="opacity-100 max-h-40"
        leave-from-class="opacity-100 max-h-40"
        leave-to-class="opacity-0 max-h-0"
      >
        <p
          v-if="expandedType === type.name"
          class="lg:hidden text-sm text-strong font-inter mt-3 overflow-hidden mr-24"
        >
          {{ type.description }}
        </p>
      </Transition>

      <!-- Desktop image -->
      <img :src="getImageSrc(type.name)" alt="" class="hidden lg:block h-32 object-contain" />
    </div>
  </div>
</template>

<script setup>
import { ref } from "vue";
import { usePurchaseFlowStore, useUIStore } from "@/stores";
import { Icon } from "@/components";
import { isRetirementAccountType } from "@/utilities";

import indiv_gold from "@/assets/images/individual_gold.png";
import indiv_blue from "@/assets/images/individual_blue.png";
import joint_gold from "@/assets/images/joint_gold.png";
import joint_blue from "@/assets/images/joint_blue.png";
import entity_gold from "@/assets/images/entity_gold.png";
import entity_blue from "@/assets/images/entity_blue.png";
import ira_gold from "@/assets/images/ira_gold.png";
import ira_blue from "@/assets/images/ira_blue.png";

const purchaseFlowStore = usePurchaseFlowStore();
const UIStore = useUIStore();

const expandedType = ref(null);

const imageMap = {
  Individual: { dark: indiv_blue, light: indiv_gold },
  Joint: { dark: joint_blue, light: joint_gold },
  Entity: { dark: entity_blue, light: entity_gold },
  IRA: { dark: ira_blue, light: ira_gold },
};

// Some rollover parent investment types (e.g. HSA, 401k) aren't one of our
// standard type tiles. For those, treat the IRA tile as the visual match so
// the user isn't shown four greyed-out options with nothing selected.
const isAliasedRolloverType = (rolloverType) => {
  return rolloverType?.includes("HSA") || rolloverType?.includes("401");
};

const rolloverMatchesType = (typeName) => {
  const rolloverType = purchaseFlowStore.selected_rollover?.type;
  if (!rolloverType) return false;
  if (isAliasedRolloverType(rolloverType)) {
    return typeName === "IRA";
  }
  return rolloverType.includes(typeName);
};

const isDisabled = (typeName) => {
  return purchaseFlowStore.selected_rollover && !rolloverMatchesType(typeName);
};

const isSelected = (type) => {
  if (purchaseFlowStore.selected_rollover) {
    return rolloverMatchesType(type.name);
  }
  return (
    purchaseFlowStore.current_investment?.type?.includes(type.name) ||
    purchaseFlowStore.current_investment?.type?.includes(type.value) ||
    // The IRA tile is also how HSA / 401(k) types get chosen, and those don't
    // contain "IRA" — without this the tile reads as unselected on the way back.
    (type.name === "IRA" && isRetirementAccountType(purchaseFlowStore.current_investment?.type))
  );
};

const getImageSrc = (typeName) => {
  const images = imageMap[typeName];
  return UIStore.is_dark_mode ? images.dark : images.light;
};

const toggleDetails = (typeName) => {
  expandedType.value = expandedType.value === typeName ? null : typeName;
};

const selectType = (typeValue) => {
  const rolloverType = purchaseFlowStore.selected_rollover?.type;
  if (purchaseFlowStore.selected_rollover) {
    if (isAliasedRolloverType(rolloverType) || !typeValue.includes("IRA")) {
      purchaseFlowStore.current_investment.type = rolloverType;
      return;
    }
    purchaseFlowStore.current_investment.type = typeValue;
    return;
  }
  // Re-selecting the IRA tile shouldn't reset a subtype (Roth IRA, HSA, ...) the
  // investor already picked on the IRA Details step.
  if (
    isRetirementAccountType(typeValue) &&
    isRetirementAccountType(purchaseFlowStore.current_investment.type)
  ) {
    return;
  }
  purchaseFlowStore.current_investment.type = typeValue;
};

const investmentTypes = [
  {
    name: "Individual",
    value: "Individual",
    description:
      "An individual account is an investment account that belongs only to you. No one else has rights or ownership over it.",
  },
  {
    name: "Joint",
    value: "Joint",
    description:
      "A joint account is an investment account that belongs to you and someone else. You can both make decisions over the assets. Examples of a joint account include spouse or partner.",
  },
  {
    name: "Entity",
    value: "Entity",
    description:
      "An entity account is an investment account that belongs to an organization or structure other than an individual or joint. This includes partnerships, corporations, trusts, etc. The structure of the entity dictates who has rights or ownership over it.",
  },
  {
    name: "IRA",
    value: "Traditional IRA",
    description:
      "An IRA is an Individual Retirement Account that provides tax advantages for your retirement savings. You can invest with Phoenix Energy through an existing Traditional, SEP, Self-Directed, Roth, or other IRA.",
  },
];

defineExpose({
  async validate() {
    return { proceed: true };
  },
  beforeContinue: () => {
    purchaseFlowStore.setInvestmentStepsForType();
    purchaseFlowStore.canFastTrackForType(purchaseFlowStore.current_investment.type);

    const type = purchaseFlowStore.current_investment.type;
    // In the step-order test group, Funding & Distribution is already the next step after
    // Investment Type for non-IRA types — the modal would only advance by one step, so suppress it.
    // IRA users are unaffected by the reorder, so their "Skip to IRA Details" modal still applies.
    const fundingIsAlreadyNext =
      purchaseFlowStore.funding_first && !isRetirementAccountType(type);

    if (purchaseFlowStore.can_fast_track && !purchaseFlowStore.has_fast_track_been_shown && !fundingIsAlreadyNext) {
      purchaseFlowStore.is_fast_track_modal_open = true;
      purchaseFlowStore.has_fast_track_been_shown = true;
      return { proceed: false };
    }
    return { proceed: true };
  },
  afterContinue: async () => {
    const promises = [
      purchaseFlowStore.saveOwnerProgress(),
      purchaseFlowStore.saveInvestmentProgress(),
    ];

    await Promise.all(promises);
  },
});
</script>
